import {
  Avatar,
  Box,
  Chip,
  Fade,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import LucideIcon from "../../../../components/LucideIcon";
import { generateAgentEmbedCode } from "../../../../utils";
import { WebsiteWidgetAgent, WebsiteWidgetResult } from "../types";
import StepNavButtons from "./StepNavButtons";

interface WebsiteWidgetsStepProps {
  widgets?: WebsiteWidgetResult;
  onNext: () => void;
  onBack: () => void;
}

function roleLabel(role?: string): string {
  if (role === "sales") return "Sales agent";
  if (role === "support") return "Support agent";
  return "Website agent";
}

// One agent card: avatar + name/role, the widget button preview, and the embed <script> with a
// copy button. The embed code is built client-side (generateAgentEmbedCode) so the API host is
// correct for this environment / whitelabel.
const WidgetCard: React.FC<{ agent: WebsiteWidgetAgent }> = ({ agent }) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const code = agent.agentId ? generateAgentEmbedCode(agent.agentId) : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked (insecure context); the code is still visible to select manually.
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar src={agent.picture || undefined} sx={{ width: 48, height: 48 }}>
          {(agent.name || "?").trim().charAt(0)}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
              {agent.name}
            </Typography>
            <Chip label={roleLabel(agent.role)} size="small" color="primary" />
          </Box>
          {agent.title && (
            <Typography variant="caption" color="text.secondary">
              {agent.title}
            </Typography>
          )}
        </Box>
        {/* The widget button the visitor would see, as a preview pill. */}
        <Chip
          icon={<LucideIcon name="MessageCircle" size={16} />}
          label={agent.buttonText || "Chat with us"}
          variant="outlined"
          sx={{ display: { xs: "none", sm: "flex" } }}
        />
      </Box>

      <Box
        sx={{
          position: "relative",
          mx: 2.5,
          mb: 2.5,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "action.hover",
        }}
      >
        <Tooltip title={copied ? "Copied!" : "Copy code"} placement="left">
          <IconButton
            size="small"
            onClick={copy}
            sx={{ position: "absolute", top: 6, right: 6 }}
          >
            <LucideIcon
              name={copied ? "Check" : "Copy"}
              size={16}
              color={copied ? theme.palette.primary.main : theme.palette.text.secondary}
            />
          </IconButton>
        </Tooltip>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            pr: 5,
            overflowX: "auto",
            fontFamily: "monospace",
            fontSize: "0.75rem",
            lineHeight: 1.6,
            color: "text.primary",
            whiteSpace: "pre",
          }}
        >
          {code}
        </Box>
      </Box>
    </Box>
  );
};

// Onboarding "deploy on your website" step: two seeded team members (Sales + Support) configured
// as website chat agents, each shown with the embed snippet to drop onto the user's site.
export default function WebsiteWidgetsStep({
  widgets,
  onNext,
  onBack,
}: WebsiteWidgetsStepProps) {
  const theme = useTheme();
  const agents = (widgets?.agents || []).filter((a) => a.agentId);

  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
          Website
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
          Put an AI agent on your website
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
          We set up two of your team members as website chat agents — one for sales, one for
          support. Drop the code snippet below into your site to go live.
        </Typography>

        {!agents.length ? (
          <Typography color="text.secondary">
            We couldn't set up website agents this time. You can deploy any agent from the Agents
            page once you're set up.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {agents.map((agent, i) => (
                <WidgetCard key={agent.agentId || i} agent={agent} />
              ))}
            </Box>
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: "action.hover",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <LucideIcon name="Code" size={20} color={theme.palette.primary.main} />
              <Typography variant="body2" color="text.secondary">
                Paste the snippet just before your site's closing &lt;/body&gt; tag. You can tweak
                or remove these agents anytime from the Agents page.
              </Typography>
            </Box>
          </>
        )}

        <StepNavButtons onBack={onBack} onNext={onNext} />
      </Box>
    </Fade>
  );
}
